import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import { ScheduledGift, scheduledGiftsService, useToast } from '@aslanpay/portal-common-lib';

import useScheduledGifts, {
  fetchFailedRecipientsAsEmployees,
  PRESELECTED_RECIPIENTS_LIMIT,
} from 'app/hooks/useScheduledGifts';
import { useRewardFlowStore } from 'app/store';
import formatNumber from 'app/utils/formatting';
import { getRewardEmployeesPath } from 'app/utils/routes';
import { getGiftStateReasonLabel, getGiftStatusDisplay } from 'app/utils/scheduledGifts';

// A retry sends in the background: the gift is checked this often, for up to this long, to say how it went.
export const RETRY_POLL_INTERVAL_MS = 3000;
export const RETRY_POLL_TIMEOUT_MS = 30000;

/** What an admin can do to a scheduled gift, shared by the list and the gift's details. */
const useScheduledGiftActions = ({ onCancelled }: { onCancelled?: () => void } = {}) => {
  const navigate = useNavigate();
  const toast = useToast();
  const { companyId = '' } = useParams();
  const { setPreSelectedEmployeeIds, setPreFilledGift } = useRewardFlowStore();
  const [giftToCancel, setGiftToCancel] = useState<ScheduledGift | undefined>();
  const [resendingGiftId, setResendingGiftId] = useState<string | undefined>();
  const queryClient = useQueryClient();

  // Checking on a retry stops when the screen closes.
  const retryPoll = useRef<ReturnType<typeof setTimeout>>();
  const isMounted = useRef(true);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      clearTimeout(retryPoll.current);
    };
  }, []);

  const refreshAfterRetry = () => {
    queryClient.invalidateQueries({ queryKey: ['fetchScheduledGifts'] });
    queryClient.invalidateQueries({ queryKey: ['fetchScheduledGift'] });
    queryClient.invalidateQueries({ queryKey: ['fetchScheduledGiftRecipients'] });
    // Sending spends points, so the company balance changes too.
    queryClient.invalidateQueries({ queryKey: ['fetchPots'] });
  };

  // Says how a retry ended, once the gift has finished sending.
  const reportRetryOutcome = (gift: ScheduledGift) => {
    if (gift.status === 'SENT') {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Gift sent',
        description: 'The points have been sent to the employees still waiting for them.',
      });
    } else if (gift.status === 'PARTIALLY_SENT') {
      toast({
        position: 'top-right',
        status: 'warning',
        title: 'Gift partly sent',
        description: `${formatNumber(gift.sentCount)} sent, ${formatNumber(
          gift.failedCount
        )} failed and ${formatNumber(
          gift.skippedCount
        )} skipped. Open the gift to see who missed out.`,
      });
    } else if (gift.status === 'FAILED') {
      toast({
        position: 'top-right',
        status: 'warning',
        title: 'Gift still not sent',
        description: getGiftStateReasonLabel(gift.stateReason) ?? 'Please try again.',
      });
    } else {
      toast({
        position: 'top-right',
        status: 'info',
        title: 'Retry finished',
        description: `The gift is now ${getGiftStatusDisplay(gift.status).label.toLowerCase()}.`,
      });
    }
  };

  // Checks the gift every few seconds until it stops sending, or gives up after a while.
  const watchRetry = (giftId: string, waitedMs = 0) => {
    retryPoll.current = setTimeout(async () => {
      let gift: ScheduledGift | undefined;
      try {
        gift = await queryClient.fetchQuery(['fetchScheduledGift', giftId], () =>
          scheduledGiftsService.fetchScheduledGift(giftId)
        );
      } catch {
        // A failed check is tried again on the next tick.
      }
      if (isMounted.current) {
        const waited = waitedMs + RETRY_POLL_INTERVAL_MS;
        if (gift && gift.status !== 'PROCESSING') {
          refreshAfterRetry();
          reportRetryOutcome(gift);
        } else if (waited >= RETRY_POLL_TIMEOUT_MS) {
          refreshAfterRetry();
          toast({
            position: 'top-right',
            status: 'info',
            title: 'Still sending',
            description: 'The gift is still being sent. Check the list again shortly.',
          });
        } else {
          watchRetry(giftId, waited);
        }
      }
    }, RETRY_POLL_INTERVAL_MS);
  };

  const { mutate: cancelGift, isLoading: isCancelling } = useScheduledGifts.useCancelScheduledGift({
    onSuccess: () => {
      toast({
        position: 'top-right',
        status: 'success',
        title: 'Gift cancelled',
        description: "The gift won't be sent.",
      });
      setGiftToCancel(undefined);
      onCancelled?.();
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Failed to cancel the gift',
        description: 'Sorry, we failed to cancel the gift. It may already be sending.',
      });
      setGiftToCancel(undefined);
    },
  });

  const {
    mutate: retryGift,
    isLoading: isRetrying,
    variables: retryingGiftId,
  } = useScheduledGifts.useRetryScheduledGift({
    // The API accepts the retry and sends in the background, handing the gift back while it's still sending.
    onSuccess: (gift) => {
      if (gift.status === 'PROCESSING') {
        toast({
          position: 'top-right',
          status: 'info',
          title: 'Retry started',
          description: "The gift is being sent. We'll let you know how it goes.",
        });
        clearTimeout(retryPoll.current);
        watchRetry(gift.id);
      } else {
        reportRetryOutcome(gift);
      }
    },
    onError: () => {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Failed to retry the gift',
        description: 'Sorry, we failed to retry the gift. Please try again.',
      });
    },
  });

  const editGift = (gift: ScheduledGift) => {
    navigate(getRewardEmployeesPath(companyId, gift.id));
  };

  // Opens the gift flow as a new gift, with the people whose payment failed and the same settings.
  const sendAgain = async (gift: ScheduledGift) => {
    if (gift.failedCount > PRESELECTED_RECIPIENTS_LIMIT) {
      toast({
        position: 'top-right',
        status: 'info',
        title: 'Too many employees to send again',
        description: `This gift failed for more than ${formatNumber(
          PRESELECTED_RECIPIENTS_LIMIT
        )} employees. Schedule a new gift for them from the gift flow instead.`,
      });
      return;
    }
    setResendingGiftId(gift.id);
    try {
      const employees = await fetchFailedRecipientsAsEmployees(gift);
      if (employees.length > 0) {
        const leftOut = gift.failedCount - employees.length;
        if (leftOut > 0) {
          toast({
            position: 'top-right',
            status: 'info',
            title: `${formatNumber(leftOut)} ${
              leftOut === 1 ? 'employee was' : 'employees were'
            } left out`,
            description:
              "They have left or are on unpaid leave, so they can't be sent the gift again now.",
          });
        }
        setPreSelectedEmployeeIds(employees.map((employee) => employee.id));
        setPreFilledGift({
          pointsAmount: gift.pointsAmount,
          transactionReason: gift.transactionReason,
          notes: gift.notes,
          labelId: gift.labelId,
          postAsRecognition: gift.postAsRecognition,
        });
        navigate(getRewardEmployeesPath(companyId));
      } else {
        toast({
          position: 'top-right',
          status: 'info',
          title: 'Nobody to send to',
          description:
            'The employees whose payment failed have since left or can no longer be gifted.',
        });
      }
    } catch {
      toast({
        position: 'top-right',
        status: 'error',
        title: 'Something went wrong',
        description: 'Sorry, we failed to load the employees for this gift. Please try again.',
      });
    } finally {
      setResendingGiftId(undefined);
    }
  };

  return {
    editGift,
    retryGift,
    isRetrying,
    retryingGiftId: isRetrying ? retryingGiftId : undefined,
    sendAgain,
    resendingGiftId,
    giftToCancel,
    askToCancel: setGiftToCancel,
    closeCancel: () => setGiftToCancel(undefined),
    confirmCancel: () => giftToCancel && cancelGift(giftToCancel.id),
    isCancelling,
  };
};

export default useScheduledGiftActions;
